// Pharmascience — AJAX mark-complete (topic + lesson pages).
//
// PROGRESSIVE ENHANCEMENT, strictly. LearnDash's own `form.sfwd-mark-complete`
// is left in the page exactly as it renders it — inputs, nonce and POST target
// untouched. This module intercepts the submit and does the same write over
// admin-ajax instead, purely to avoid the full page reload. With JS off, absent,
// or broken, the native POST still completes the step, which is why every
// failure path here ends in either a restored button or a native submit.
//
// ── THE CHIP HAS ONE VISUAL DEFINITION ──────────────────────────────────────
// The completed chip is normally emitted server-side by
// `pharmascience_step_complete_chip()` (inc/child_learndash.php). After an ajax
// completion there is no re-render, so the chip has to be built here — to the
// SAME shape, with the class and label supplied from PHP via
// `bnrmpMarkComplete` rather than duplicated as literals. That way the label
// stays translatable and the classes cannot drift apart in a later edit.
//
// ⚠️ ONE DELIBERATE DIFFERENCE from the PHP chip: the icon. The PHP chip uses a
// bare stroked tick; this one uses the green-circle-white-tick from the
// dashboard module card, per the operator's spec for the post-ajax state, and
// carries the extra `--done` modifier so SCSS can give it the navy background.
// The difference is intentional and lives in exactly these two places (the
// `chipClass` value in PHP, and the SVG below) — not scattered.
//
// 🔴 PRESENTATION + TRANSPORT ONLY. Nothing here decides whether the step may
// be completed. Every authorization decision — nonce, post type, course access,
// progression — is made server-side in `pharmascience_ajax_mark_complete()`.
// This module cannot grant a completion the form POST would have refused: it
// forwards LearnDash's own nonce and gets back a 403 if the server says no.

const SELECTOR = 'form.sfwd-mark-complete';

const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * The green-circle-white-tick icon, built node by node.
 *
 * Deliberately NOT `innerHTML`, even though the markup is a fixed literal with
 * nothing interpolated into it. Constructing the nodes means this function
 * contains no HTML-parsing sink at all, so a later edit cannot turn it into one
 * by dropping a variable into the string. Costs a few lines; removes the whole
 * category of mistake.
 *
 * @return {SVGSVGElement} The icon.
 */
function buildIcon() {
	const svg = document.createElementNS( SVG_NS, 'svg' );
	svg.setAttribute( 'width', '20' );
	svg.setAttribute( 'height', '20' );
	svg.setAttribute( 'viewBox', '0 0 24 24' );
	svg.setAttribute( 'fill', 'none' );
	svg.setAttribute( 'aria-hidden', 'true' );

	const circle = document.createElementNS( SVG_NS, 'circle' );
	circle.setAttribute( 'cx', '12' );
	circle.setAttribute( 'cy', '12' );
	circle.setAttribute( 'r', '10' );
	circle.setAttribute( 'fill', 'currentColor' );

	const tick = document.createElementNS( SVG_NS, 'path' );
	tick.setAttribute( 'd', 'M8 12.5l2.5 2.5L16 9.5' );
	tick.setAttribute( 'stroke', '#FFFFFF' );
	tick.setAttribute( 'stroke-width', '2' );
	tick.setAttribute( 'stroke-linecap', 'round' );
	tick.setAttribute( 'stroke-linejoin', 'round' );

	svg.appendChild( circle );
	svg.appendChild( tick );

	return svg;
}

/**
 * The completed chip, matching the server-rendered one.
 *
 * The label is translated copy arriving from PHP. It is inserted as a TEXT
 * node, so it can never be parsed as markup regardless of what a translator
 * puts in the catalogue.
 *
 * @param {string} label     Translated "Completed" string.
 * @param {string} chipClass Class list supplied by PHP.
 * @return {HTMLParagraphElement} The chip element.
 */
function buildChip( label, chipClass ) {
	const chip = document.createElement( 'p' );
	chip.className = chipClass;
	// role="status" so the swap is announced — the learner may have been
	// waiting on the request and never sees a page change otherwise.
	chip.setAttribute( 'role', 'status' );

	const icon = document.createElement( 'span' );
	icon.className = 'bnrmp-step-complete__icon';
	icon.setAttribute( 'aria-hidden', 'true' );
	icon.appendChild( buildIcon() );

	chip.appendChild( icon );
	chip.appendChild( document.createTextNode( label ) );

	return chip;
}

/**
 * Show an error next to the button, accessibly.
 *
 * Browser dialogs are banned in this environment, so the message goes into a
 * live region beside the control instead. Reused across attempts rather than
 * appended each time, so repeated failures do not stack up messages.
 *
 * @param {HTMLFormElement} form    The mark-complete form.
 * @param {string}          message Text to announce.
 * @return {void}
 */
function showError( form, message ) {
	let region = form.querySelector( '.bnrmp-step-complete__error' );

	if ( ! region ) {
		region = document.createElement( 'p' );
		region.className = 'bnrmp-step-complete__error';
		// assertive: the learner pressed a button and the action failed, so
		// this must interrupt rather than wait for a pause in speech.
		region.setAttribute( 'role', 'alert' );
		form.appendChild( region );
	}

	region.textContent = message;
}

export function initMarkCompleteAjax() {
	const form = document.querySelector( SELECTOR );
	if ( ! form ) return;

	// No localized payload means no endpoint and no translated strings. Bail
	// and leave the native POST as the only path — never guess an ajax URL or
	// fall back to English literals.
	const cfg = window.bnrmpMarkComplete;
	if ( ! cfg || ! cfg.ajaxUrl || ! cfg.action ) return;

	const strings = cfg.strings || {};

	form.addEventListener( 'submit', ( event ) => {
		const button = form.querySelector( 'input[type="submit"], button[type="submit"]' );
		const nonceField = form.querySelector( 'input[name="sfwd_mark_complete"]' );
		const postField = form.querySelector( 'input[name="post"]' );

		// If the form is not the shape we expect — LearnDash changed it, or a
		// plugin rewrote it — do NOT intercept. Letting the native POST proceed
		// is always safer than submitting a request we cannot form correctly.
		if ( ! button || ! nonceField || ! postField ) return;

		event.preventDefault();

		// Guard against a double submit while the first is still in flight.
		if ( form.dataset.bnrmpBusy === '1' ) return;
		form.dataset.bnrmpBusy = '1';

		// `value` for an <input type="submit">, textContent for a <button>.
		const isInput = button.tagName === 'INPUT';
		const originalLabel = isInput ? button.value : button.textContent;

		const restore = () => {
			delete form.dataset.bnrmpBusy;
			button.disabled = false;
			button.removeAttribute( 'aria-busy' );
			if ( isInput ) {
				button.value = originalLabel;
			} else {
				button.textContent = originalLabel;
			}
		};

		// Loading state.
		button.disabled = true;
		button.setAttribute( 'aria-busy', 'true' );
		if ( strings.marking ) {
			if ( isInput ) {
				button.value = strings.marking;
			} else {
				button.textContent = strings.marking;
			}
		}

		const body = new URLSearchParams();
		body.append( 'action', cfg.action );
		// LearnDash's own user+post-scoped nonce, forwarded from the form. No
		// user id is sent — the server takes the writer from the session.
		body.append( 'nonce', nonceField.value );
		body.append( 'post_id', postField.value );

		fetch( cfg.ajaxUrl, {
			method: 'POST',
			// Cookies are what authenticate the request; admin-ajax has no
			// other notion of the current user.
			credentials: 'same-origin',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
				'X-Requested-With': 'XMLHttpRequest',
			},
			body: body.toString(),
		} )
			.then( ( response ) => response.json().catch( () => null ) )
			.then( ( payload ) => {
				if ( ! payload || ! payload.success || ! payload.data ) {
					// A specific server message is more useful than the generic
					// one, so prefer it when present.
					const message =
						( payload && payload.data && payload.data.message ) ||
						strings.error ||
						'';
					restore();
					showError( form, message );
					return;
				}

				// Success — swap the form for the chip. Replacing the form is
				// what removes the (now meaningless) button; the step is
				// already complete server-side, so there is nothing left to
				// submit.
				const chip = buildChip(
					strings.completed || '',
					cfg.chipClass || 'bnrmp-step-complete bnrmp-step-complete--done'
				);

				if ( form.parentNode ) {
					form.parentNode.replaceChild( chip, form );
				}

				// Move focus to the chip so a keyboard user is not stranded on
				// a button that no longer exists.
				chip.setAttribute( 'tabindex', '-1' );
				chip.focus( { preventScroll: true } );
			} )
			.catch( () => {
				// Network/transport failure. The completion may not have been
				// written, so hand the learner back to the path that definitely
				// works: resubmit natively. `submit()` does not re-fire this
				// listener, so there is no loop.
				delete form.dataset.bnrmpBusy;
				form.submit();
			} );
	} );
}
